import React from 'react';
import { Anchor, Group, Paper, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { CalendarItem } from '../../hooks/useCalendarApi';
import { zonedDay } from './layoutMonth';
import { CalendarItemRow } from './CalendarItemRow';
import './calendar.css';

/**
 * Список по дням: на телефоне сетка месяца нечитаема, поэтому он там по
 * умолчанию. `onlyDay` приходит из сетки, когда человек нажал «ещё N» в
 * конкретной клетке: показываем этот день, а не все шесть недель подряд.
 */
export const AgendaList: React.FC<{
  items: CalendarItem[];
  timeZone: string;
  showPerson: boolean;
  onlyDay?: string | null;
  onClearDay?: () => void;
}> = ({ items, timeZone, showPerson, onlyDay, onClearDay }) => {
  const { t } = useTranslation();

  const groups = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const day = zonedDay(item.start, timeZone);
    if (onlyDay && day !== onlyDay) continue;
    groups.set(day, [...(groups.get(day) ?? []), item]);
  }

  const today = zonedDay(new Date().toISOString(), timeZone);

  if (groups.size === 0) {
    return (
      <Stack gap="xs">
        {onlyDay && onClearDay && (
          <Anchor component="button" type="button" size="sm" onClick={onClearDay}>
            {t('calendar.page.showAllDays')}
          </Anchor>
        )}
        <Text c="dimmed">{t('calendar.page.empty')}</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {onlyDay && onClearDay && (
        <Anchor
          component="button"
          type="button"
          size="sm"
          onClick={onClearDay}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('calendar.page.showAllDays')}
        </Anchor>
      )}

      {Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([day, list]) => (
          <Paper key={day} withBorder radius="md" p="sm">
            <Group gap={8} align="baseline" mb={6}>
              <Text
                size="sm"
                fw={600}
                c={day === today ? 'teal' : undefined}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {dayjs(day).format('D MMMM')}
              </Text>
              <Text size="xs" c="dimmed">
                {dayjs(day).format('dddd')}
              </Text>
            </Group>
            <Stack gap={4}>
              {list.map((item) => (
                <CalendarItemRow key={item.id} item={item} showPerson={showPerson} />
              ))}
            </Stack>
          </Paper>
        ))}
    </Stack>
  );
};
